import { useState, useEffect } from "react";
import api from "../../../utils/api";

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    OutlinedInput,
    FormHelperText,
    Button,
    Box,
    FormControl,
    FormLabel,
    CircularProgress,
    Tabs,
    Tab,
    Avatar,
    Typography,
    IconButton,
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import QuillEditor from "../../../components/QuillEditor";

const languages = ['hy', 'en', 'ru'];
const languageLabels = {
    'hy': 'Armenian',
    'en': 'English',
    'ru': 'Russian'
};

const textFields = ['name', 'description', 'meta_keywords', 'meta_description'];

const emptyForm = () => {
    const initial = { thumbnail: null, event_date: '' };
    languages.forEach(lang => {
        textFields.forEach(field => {
            initial[`${field}_${lang}`] = '';
        });
    });
    return initial;
};

export default function EventModal({ open, onClose, row = null }) {
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [sectionTab, setSectionTab] = useState(0);
    const [activeTab, setActiveTab] = useState(0);
    const [form, setForm] = useState(emptyForm);
    const [imagePreview, setImagePreview] = useState(null);
    const [createdId, setCreatedId] = useState(null);
    const [mediaItems, setMediaItems] = useState([]);
    const [pendingImages, setPendingImages] = useState([]);
    const [pendingVideos, setPendingVideos] = useState([]);
    const [videoUrl, setVideoUrl] = useState('');
    const [posterFile, setPosterFile] = useState(null);
    const [posterPreview, setPosterPreview] = useState(null);
    const [mediaBusy, setMediaBusy] = useState(false);

    const eventId = row?.id || createdId;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleDescriptionChange = (language, value) => {
        setForm((prev) => ({ ...prev, [`description_${language}`]: value }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setForm((prev) => ({ ...prev, thumbnail: file }));
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const appendMedia = (items) => {
        if (!Array.isArray(items) || items.length === 0) return;
        setMediaItems((prev) => [...prev, ...items]);
    };

    const uploadMedia = async (id, payload) => {
        const formData = new FormData();
        (payload.images || []).forEach((file) => formData.append('images[]', file));
        (payload.videos || []).forEach((file) => formData.append('videos[]', file));
        if (payload.videoUrl) {
            formData.append('video_url', payload.videoUrl);
        }
        if (payload.poster) {
            formData.append('poster', payload.poster);
        }

        const response = await api.post(`admin/events/${id}/media`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        appendMedia(response.data.data);
    };

    const handleSubmit = async () => {
        try {
            setSaving(true);
            setErrors({});

            const formData = new FormData();
            languages.forEach(lang => {
                textFields.forEach(field => {
                    const key = `${field}_${lang}`;
                    if (form[key]) {
                        formData.append(key, form[key]);
                    }
                });
            });
            if (form.event_date) {
                formData.append('event_date', form.event_date);
            }

            if (form.thumbnail instanceof File) {
                formData.append('thumbnail', form.thumbnail);
            } else if (!eventId && !form.thumbnail) {
                setErrors({ thumbnail: ['Thumbnail is required'] });
                setSectionTab(0);
                setSaving(false);
                return;
            }

            let savedId = eventId;
            if (savedId) {
                await api.post(`admin/events/${savedId}/update`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
            } else {
                const response = await api.post("admin/events", formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                savedId = response.data.data.id;
                setCreatedId(savedId);
            }

            const hasPending = pendingImages.length > 0 || pendingVideos.length > 0 || (videoUrl && posterFile);
            if (!row && hasPending) {
                await uploadMedia(savedId, {
                    images: pendingImages,
                    videos: pendingVideos,
                    videoUrl: videoUrl && posterFile ? videoUrl : '',
                    poster: videoUrl && posterFile ? posterFile : null,
                });
                setPendingImages([]);
                setPendingVideos([]);
                setVideoUrl('');
                setPosterFile(null);
                setPosterPreview(null);
            }

            onClose(true);
        } catch (e) {
            if (e.errors) {
                setErrors(e.errors);
            } else if (e.response?.data?.errors) {
                setErrors(e.response.data.errors);
            }
        } finally {
            setSaving(false);
        }
    };

    const handleAddFiles = (kind, fileList) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;
        if (kind === 'images') {
            setPendingImages((prev) => [...prev, ...files]);
        } else {
            setPendingVideos((prev) => [...prev, ...files]);
        }
    };

    const handleUploadNow = async (kind, fileList) => {
        const files = Array.from(fileList || []);
        if (!eventId || files.length === 0) return;
        try {
            setMediaBusy(true);
            setErrors({});
            await uploadMedia(eventId, kind === 'images' ? { images: files } : { videos: files });
        } catch (e) {
            if (e.errors) setErrors(e.errors);
            else if (e.response?.data?.errors) setErrors(e.response.data.errors);
        } finally {
            setMediaBusy(false);
        }
    };

    const handleAddVideoUrl = async () => {
        if (!videoUrl || !posterFile) {
            setErrors({ video_url: ['Video URL and a poster image are both required'] });
            return;
        }
        if (!eventId) return;
        try {
            setMediaBusy(true);
            setErrors({});
            await uploadMedia(eventId, { videoUrl, poster: posterFile });
            setVideoUrl('');
            setPosterFile(null);
            setPosterPreview(null);
        } catch (e) {
            if (e.errors) setErrors(e.errors);
            else if (e.response?.data?.errors) setErrors(e.response.data.errors);
        } finally {
            setMediaBusy(false);
        }
    };

    const handleDeleteMedia = async (mediaId) => {
        if (!eventId) return;
        try {
            setMediaBusy(true);
            await api.delete(`admin/events/${eventId}/media/${mediaId}`);
            setMediaItems((prev) => prev.filter((item) => item.id !== mediaId));
        } catch (e) {
            console.error(e);
        } finally {
            setMediaBusy(false);
        }
    };

    const handlePosterChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setPosterFile(file);
        const reader = new FileReader();
        reader.onloadend = () => setPosterPreview(reader.result);
        reader.readAsDataURL(file);
    };

    useEffect(() => {
        if (!open) return;

        if (row) {
            const newForm = emptyForm();
            languages.forEach(lang => {
                textFields.forEach(field => {
                    newForm[`${field}_${lang}`] = row[`${field}_${lang}`] || '';
                });
            });
            newForm.event_date = row.event_date ? String(row.event_date).slice(0, 10) : '';
            setForm(newForm);
            setImagePreview(row.thumbnail_url || null);
            setMediaItems(Array.isArray(row.media) ? row.media : []);
        } else {
            setForm(emptyForm());
            setImagePreview(null);
            setMediaItems([]);
        }
        setErrors({});
        setSaving(false);
        setSectionTab(0);
        setActiveTab(0);
        setCreatedId(null);
        setPendingImages([]);
        setPendingVideos([]);
        setVideoUrl('');
        setPosterFile(null);
        setPosterPreview(null);
    }, [row, open]);

    const currentLanguage = languages[activeTab];
    const fieldError = (key) => errors[key]?.[0];

    return (
        <Dialog open={open} onClose={() => onClose()} maxWidth="md" fullWidth>
            <DialogTitle>{!row ? "Create Event" : "Edit Event"}</DialogTitle>
            <DialogContent>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                    <Tabs value={sectionTab} onChange={(e, value) => setSectionTab(value)}>
                        <Tab label="Details" />
                        <Tab label="Media" />
                    </Tabs>
                </Box>

                {sectionTab === 0 && (
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                                {languages.map((lang) => (
                                    <Tab key={lang} label={languageLabels[lang]} />
                                ))}
                            </Tabs>
                        </Box>

                        <FormControl error={!!fieldError('thumbnail')}>
                            <FormLabel htmlFor="thumbnail">Thumbnail {!row && '*'}</FormLabel>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                {imagePreview && (
                                    <Avatar
                                        variant="rounded"
                                        src={imagePreview}
                                        sx={{ width: 100, height: 100 }}
                                    />
                                )}
                                <Button variant="outlined" component="label">
                                    {row && !(form.thumbnail instanceof File) ? 'Change Thumbnail' : 'Upload Thumbnail'}
                                    <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                                </Button>
                            </Box>
                            {fieldError('thumbnail') && (
                                <FormHelperText sx={{ m: 0.5 }} error>{fieldError('thumbnail')}</FormHelperText>
                            )}
                        </FormControl>

                        <FormControl error={!!fieldError('event_date')}>
                            <FormLabel htmlFor="event_date">Date *</FormLabel>
                            <OutlinedInput
                                name="event_date"
                                type="date"
                                fullWidth
                                value={form.event_date || ''}
                                onChange={handleChange}
                            />
                            {fieldError('event_date') && (
                                <FormHelperText sx={{ m: 0.5 }} error>{fieldError('event_date')}</FormHelperText>
                            )}
                        </FormControl>

                        <FormControl error={!!fieldError(`name_${currentLanguage}`)}>
                            <FormLabel htmlFor={`name_${currentLanguage}`}>Name *</FormLabel>
                            <OutlinedInput
                                name={`name_${currentLanguage}`}
                                fullWidth
                                value={form[`name_${currentLanguage}`] || ''}
                                onChange={handleChange}
                            />
                            {fieldError(`name_${currentLanguage}`) && (
                                <FormHelperText sx={{ m: 0.5 }} error>{fieldError(`name_${currentLanguage}`)}</FormHelperText>
                            )}
                        </FormControl>

                        <FormControl error={!!fieldError(`description_${currentLanguage}`)} fullWidth>
                            <FormLabel sx={{ mb: 1 }}>Description ({languageLabels[currentLanguage]}) *</FormLabel>
                            <QuillEditor
                                key={`description-editor-${currentLanguage}`}
                                value={form[`description_${currentLanguage}`] || ''}
                                onChange={(value) => handleDescriptionChange(currentLanguage, value)}
                                placeholder={`Write ${languageLabels[currentLanguage]} description...`}
                            />
                            {fieldError(`description_${currentLanguage}`) && (
                                <FormHelperText sx={{ m: 0.5 }} error>{fieldError(`description_${currentLanguage}`)}</FormHelperText>
                            )}
                        </FormControl>

                        <FormControl error={!!fieldError(`meta_keywords_${currentLanguage}`)}>
                            <FormLabel>Meta Keywords</FormLabel>
                            <OutlinedInput
                                name={`meta_keywords_${currentLanguage}`}
                                fullWidth
                                value={form[`meta_keywords_${currentLanguage}`] || ''}
                                onChange={handleChange}
                            />
                            {fieldError(`meta_keywords_${currentLanguage}`) && (
                                <FormHelperText sx={{ m: 0.5 }} error>{fieldError(`meta_keywords_${currentLanguage}`)}</FormHelperText>
                            )}
                        </FormControl>

                        <FormControl error={!!fieldError(`meta_description_${currentLanguage}`)}>
                            <FormLabel>Meta Description</FormLabel>
                            <OutlinedInput
                                name={`meta_description_${currentLanguage}`}
                                fullWidth
                                multiline
                                rows={3}
                                value={form[`meta_description_${currentLanguage}`] || ''}
                                onChange={handleChange}
                            />
                            {fieldError(`meta_description_${currentLanguage}`) && (
                                <FormHelperText sx={{ m: 0.5 }} error>{fieldError(`meta_description_${currentLanguage}`)}</FormHelperText>
                            )}
                        </FormControl>
                    </Box>
                )}

                {sectionTab === 1 && (
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        {!eventId && (
                            <Typography variant="body2" color="text.secondary">
                                Files selected here are uploaded when you create the event.
                            </Typography>
                        )}

                        {fieldError('media') && (
                            <FormHelperText error>{fieldError('media')}</FormHelperText>
                        )}

                        {mediaItems.length > 0 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {mediaItems.map((item) => (
                                    <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        {item.type === 'image' || item.video_url ? (
                                            <Avatar variant="rounded" src={item.url} sx={{ width: 64, height: 64 }} />
                                        ) : (
                                            <Avatar variant="rounded" sx={{ width: 64, height: 64 }}>Video</Avatar>
                                        )}
                                        <Box sx={{ flexGrow: 1 }}>
                                            <Typography variant="body2">
                                                {item.type === 'video' ? 'Video' : 'Image'}
                                            </Typography>
                                            {item.video_url && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {item.video_url}
                                                </Typography>
                                            )}
                                        </Box>
                                        {eventId && (
                                            <IconButton aria-label="Delete media" onClick={() => handleDeleteMedia(item.id)} disabled={mediaBusy}>
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        )}

                        <FormControl>
                            <FormLabel>Photos</FormLabel>
                            <Button variant="outlined" component="label" disabled={mediaBusy} sx={{ mt: 1, alignSelf: 'flex-start' }}>
                                {eventId ? 'Upload Photos' : 'Select Photos'}
                                <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    multiple
                                    onChange={(e) => {
                                        if (eventId) handleUploadNow('images', e.target.files);
                                        else handleAddFiles('images', e.target.files);
                                        e.target.value = '';
                                    }}
                                />
                            </Button>
                            {!eventId && pendingImages.length > 0 && (
                                <Typography variant="caption">{pendingImages.length} photo(s) selected</Typography>
                            )}
                        </FormControl>

                        <FormControl error={!!fieldError('videos') || !!(errors['videos.0'])}>
                            <FormLabel>Video files</FormLabel>
                            <Button variant="outlined" component="label" disabled={mediaBusy} sx={{ mt: 1, alignSelf: 'flex-start' }}>
                                {eventId ? 'Upload Videos' : 'Select Videos'}
                                <input
                                    type="file"
                                    hidden
                                    accept="video/mp4,video/webm"
                                    multiple
                                    onChange={(e) => {
                                        if (eventId) handleUploadNow('videos', e.target.files);
                                        else handleAddFiles('videos', e.target.files);
                                        e.target.value = '';
                                    }}
                                />
                            </Button>
                            {!eventId && pendingVideos.length > 0 && (
                                <Typography variant="caption">{pendingVideos.length} video(s) selected</Typography>
                            )}
                        </FormControl>

                        <FormControl error={!!fieldError('video_url') || !!fieldError('poster')}>
                            <FormLabel>Video URL</FormLabel>
                            <OutlinedInput
                                fullWidth
                                placeholder="https://"
                                value={videoUrl}
                                onChange={(e) => setVideoUrl(e.target.value)}
                                sx={{ mt: 1 }}
                            />
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1 }}>
                                {posterPreview && (
                                    <Avatar variant="rounded" src={posterPreview} sx={{ width: 64, height: 64 }} />
                                )}
                                <Button variant="outlined" component="label">
                                    Poster image
                                    <input type="file" hidden accept="image/*" onChange={handlePosterChange} />
                                </Button>
                                {eventId && (
                                    <Button variant="contained" onClick={handleAddVideoUrl} disabled={mediaBusy || !videoUrl || !posterFile}>
                                        Add
                                    </Button>
                                )}
                            </Box>
                            {!eventId && videoUrl && posterFile && (
                                <Typography variant="caption">Video URL will be saved with the event.</Typography>
                            )}
                            {(fieldError('video_url') || fieldError('poster')) && (
                                <FormHelperText error>{fieldError('video_url') || fieldError('poster')}</FormHelperText>
                            )}
                        </FormControl>

                        {mediaBusy && <CircularProgress size={24} />}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose()}>Cancel</Button>
                <Button
                    sx={{ color: '#fff' }}
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={saving}
                >
                    {saving ? <CircularProgress size={24} /> : (!row ? "Create" : "Update")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
